import React from 'react'

import PropTypes from 'prop-types'

import { useIntl } from 'react-intl'

import { useRecoilValue, useRecoilCallback } from 'recoil'

import { modalBeeContentDialogState, templates } from '../recoil/state'
import {
  onChangeContentsPerRowCallback,
  onChangeTemplateCallback
} from '../recoil/callbacks'

import {
  IMG_POS_TOP,
  IMG_POS_LEFT,
  IMG_POS_RIGHT,
  IMG_SPACE_SMALL
} from '../constants'

import svgload from 'Helpers/svgload'

import styled from '@emotion/styled'

import IconCheck from 'mailup-web-components/src/img/icons/check.svg?raw'
import IconImage from 'mailup-web-components/src/img/icons/image.svg?raw'

const StyledNotes = styled.p`
  color: var(--boulder);
  font-size: 15px;
  font-weight: 400;
`

const TemplatesContainer = styled.div`
  margin-top: 10px;
  margin-bottom: 20px;
  display: flex;
`

const TemplateItemContainer = styled.div`
  flex-basis: 20%;
`

const StyledTemplateItem = styled.div`
  position: relative;
  margin-right: 15px;

  ${({ checked }) =>
    checked
      ? `border: 2px solid var(--java);
        &::before {
        position: absolute;
        top: 10px;
        right: -11px;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        border: 2px solid var(--java);
        content: " ";

        background-color: var(--java);
        background-image: ${svgload(IconCheck, { path: { fill: '#fff' } })};
        background-repeat: no-repeat;
        background-size: 13px;
        background-position:center;
      }
      
      `
      : `border: 2px solid #969696;`}

  & input[type= 'radio' ] {
    display: none;
  }
`

const LayoutPreviewContainer = styled.ul`
  display: flex;
  background-color: #dedede;
  padding: 2px;

  ${({ imgPos }) =>
    imgPos === IMG_POS_LEFT &&
    `
      &::before {
        margin: 2px;
        padding: 4px;
        background-color: #fff;

        background-image: ${svgload(IconImage, { path: { fill: '#7c7c7c' } })};
        background-size: 28px 28px;
        background-repeat: no-repeat;

        background-position: center center;

        content: " ";
        display: flex;
        flex-grow: 2;
      }
    `}

    ${({ imgPos }) =>
      imgPos === IMG_POS_RIGHT &&
      `
      &::after {
        margin: 2px;
        padding: 4px;
        background-color: #fff;

        background-image: ${svgload(IconImage, { path: { fill: '#7c7c7c' } })};
        background-size: 28px 28px;
        background-repeat: no-repeat;

        background-position: center center;

        content: " ";
        display: flex;
        flex-grow: 2;
      }
   `}

    ${({ imgSpace }) =>
      imgSpace === IMG_SPACE_SMALL &&
      `
      &::before,
      &::after {
        flex-grow: 1;
      }
      
    `}
`

const LayoutPreviewContent = styled.li`
  flex-grow: 2;
  margin: 2px;
  text-align: center;

  height: 4rem;
  line-height: 3rem;
  padding: 0.5rem;
  font-size: 22px;
  background-color: #fff;
  color: var(--boulder);

  align-items: center;
  justify-content: center;

  ${({ imgPos }) =>
    imgPos === IMG_POS_TOP &&
    `
    
      background-image: ${svgload(IconImage, { path: { fill: '#7c7c7c' } })};
      background-size: 28px 28px;
      background-repeat: no-repeat;

      background-position: center 8px;
    
  `}
`

const TemplateItem = ({ template }) => {
  const { contentsPerRow } = useRecoilValue(modalBeeContentDialogState)
  const onChangeCallback = useRecoilCallback(onChangeContentsPerRowCallback, [])
  const handleChange = ({ target: { value } }) =>
    onChangeCallback(parseInt(value))
  const checked = contentsPerRow === template.contentsPerRow

  const itemId = `contend-dialog-template-item-${template.id}`

  const sampleContents = Array.from(
    { length: template.contentsPerRow },
    (_, i) => i + 1
  )

  return (
    <TemplateItemContainer>
      <StyledTemplateItem checked={checked}>
        <input
          type='radio'
          value={template.contentsPerRow}
          checked={checked}
          id={itemId}
          onChange={handleChange}
        />
        <label htmlFor={itemId}>
          <LayoutPreviewContainer>
            {sampleContents.map((n, idx) => (
              <LayoutPreviewContent key={idx}>{n}</LayoutPreviewContent>
            ))}
          </LayoutPreviewContainer>
        </label>
      </StyledTemplateItem>
    </TemplateItemContainer>
  )
}

TemplateItem.propTypes = {
  template: PropTypes.shape({
    id: PropTypes.number,
    contentsPerRow: PropTypes.number
  })
}

const TemplateImageItem = ({ template: { imgPos, imgSpace, id } }) => {
  const { imgPos: selectedImgPos, imgSpace: selectedImgSpace } = useRecoilValue(
    modalBeeContentDialogState
  )

  const onChangeCallback = useRecoilCallback(onChangeTemplateCallback, [])
  const handleChange = () => onChangeCallback(id)
  const checked = imgPos === selectedImgPos && imgSpace === selectedImgSpace
  const itemId = `contend-dialog-template-image-item-${id}`

  return (
    <TemplateItemContainer>
      <StyledTemplateItem checked={checked}>
        <input
          type='radio'
          value={id}
          checked={checked}
          id={itemId}
          onChange={handleChange}
        />
        <label htmlFor={itemId}>
          <LayoutPreviewContainer imgPos={imgPos} imgSpace={imgSpace}>
            <LayoutPreviewContent imgPos={imgPos} imgSpace={imgSpace} />
          </LayoutPreviewContainer>
        </label>
      </StyledTemplateItem>
    </TemplateItemContainer>
  )
}

export const LayoutInputContentsPerRow = () => {
  const intl = useIntl()

  const noImageTemplates = Object.values(templates).filter(
    ({ imgPos }) => !imgPos
  )

  return (
    <>
      <StyledNotes>
        {intl.formatMessage({
          id: 'Load.BeeContentDialog.SelectTemplateNotes'
        })}
      </StyledNotes>
      <TemplatesContainer>
        <TemplateItem template={templates[1]} />
        {noImageTemplates.map(o => (
          <TemplateItem key={o.id} template={o} />
        ))}
      </TemplatesContainer>
    </>
  )
}

TemplateImageItem.propTypes = {
  template: PropTypes.shape({
    id: PropTypes.number,
    imgPos: PropTypes.string,
    imgSpace: PropTypes.string
  })
}

export const LayoutInputImagePosition = () => {
  const intl = useIntl()
  const { contentsPerRow } = useRecoilValue(modalBeeContentDialogState)
  if (contentsPerRow !== 1) return null

  const withImage = Object.values(templates).filter(({ imgPos }) => !!imgPos)

  return (
    <>
      <StyledNotes>
        {intl.formatMessage({
          id: 'Load.BeeContentDialog.SelectTemplateOptionsNotes'
        })}
      </StyledNotes>
      <TemplatesContainer>
        {withImage.map(o => (
          <TemplateImageItem key={o.id} template={o} />
        ))}
      </TemplatesContainer>
    </>
  )
}
